import { globalFilesEntityId, User, UserIdentification } from "@sp-crm/core";
import { ClientSearchSort, TwoFactorMethod } from "generated/graphql";
import { QueryClient } from "react-query";
import { Action, Dispatch } from "redux";
import { push } from "util/history";
import { identifyUser, logoutEvent } from "util/user-events";
import { GetUserTenantRegionOptionsQuery } from "../../generated/graphql";
import * as browser from "../../util/browser";
import * as http from "../../util/http";
import { handleLoginCompletion } from "../reducers/login";
import { clearFileCacheAction } from "./files";
import { Actions, loadAll, navigate, updateUser } from "./index";
import { createChangeRegionAction } from "./region";

interface SessionResponse {
    requiresTwoFactor?: boolean;
    userId?: string;
    methods?: TwoFactorMethod[];
    method?: TwoFactorMethod;
    codeSent?: boolean;
    userRecord?: {
        signedEula?: Date | string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export const switchUser = async (
    option: GetUserTenantRegionOptionsQuery["getUserTenantRegionOptions"]["options"][0],
    queryClient: QueryClient,
    dispatch: Dispatch<Action>,
) => {
    const requestDescription = http.newRequestDescription();
    const payload = {
        tenantId: option.tenantId,
        userId: option.userId,
    };
    await http.put(requestDescription, `/api/session`, payload);
    dispatch(createChangeRegionAction(option.regionKey));

    // Needed because tenant-wide files use globalFilesEntityId (which is the same for all tenants)
    dispatch(clearFileCacheAction(globalFilesEntityId));

    loadAll(queryClient, dispatch);
    navigate("/");
};

export async function loadSignedInUser(dispatch: Dispatch) {
    const description = http.newRequestDescription();
    try {
        const response = await http.get<UserIdentification>(description, "/api/user/ids");
        const data = response.data;
        if (data) identifyUser(data);
    } catch (e) {
        http.errorHandler(e, description, dispatch);
    }
}

export function clientLogout(dispatch: Dispatch<Action>, reason?: string): void {
    browser.clearCredentials();
    logoutEvent();
    dispatch({ type: Actions[Actions.LOGOUT] });
    push(reason ? `/login?reason=${reason}` : "/login");
}

export function logout(dispatch: Dispatch<Action>, reason?: string): void {
    http.send(http.newRequestDescription(), "DELETE", "/api/session")
        .catch(() => {})
        .finally(() => {
            clientLogout(dispatch, reason);
        });
}

export function signEula(
    payload: browser.ILoginResponse,
    queryClient: QueryClient,
    dispatch: Dispatch<Action>,
) {
    browser.handleLogin(payload);
    const userRecord = { signedEula: new Date() };
    updateUser(userRecord as User, dispatch);
    loadAll(queryClient, dispatch);
    dispatch({ type: Actions[Actions.LOGIN_FINISH], user: payload });
    push("/");
}

interface DashboardSortPayload {
    cardSort: ClientSearchSort;
}

export type DashboardSortAction = DashboardSortPayload & Action;

export function login(
    credentials: browser.LoginCredentials,
    queryClient: QueryClient,
    dispatch: Dispatch<Action>,
): void {
    dispatch({ type: Actions[Actions.LOGIN_START] });
    http.post<SessionResponse>(http.newRequestDescription(), "/api/session", credentials)
        .then(r => r.data)
        .then(r => {
            if (r.requiresTwoFactor) {
                dispatch({
                    type: Actions[Actions.LOGIN_REQUIRES_2FA],
                    twoFactorData: r,
                });
                push("/two-factor-verification");
            } else {
                handleLoginCompletion(r, dispatch, queryClient);
            }
        })
        .catch(e => {
            dispatch({
                type: Actions[Actions.LOGIN_FAIL],
                message:
                    typeof e?.response?.data?.err === "string"
                        ? e.response.data.err
                        : "An unexpected error happed on login. Try again later or contact support for help.",
            });
        });
}

export function startHeartbeats(dispatch: Dispatch<Action>): void {
    const heartbeat = async () => {
        try {
            const result = await http.get(
                http.newRequestDescription(),
                "/api/session/heartbeat",
            );
            const payload = {
                type: Actions[Actions.HEARTBEAT],
                now: new Date(),
                data: result.data,
            };
            dispatch(payload);
        } catch (e) {
            console.warn("Error connecting to Heartbeat API");
        }
    };
    setInterval(heartbeat, 1000 * 60 * 10);
    heartbeat();
}

export async function extendSession(): Promise<void> {
    await http.get(http.newRequestDescription(), "/api/session/heartbeat");
}
